// The two loops that copy settings between this device's in-memory settings and
// the synced `settings` document in the user's database (deferred finding
// df-f6429b6b690d).
//
// **Why they live here and not in the component that calls them.** These loops
// used to sit inside `app.component.ts`, which is thousands of lines long and
// cannot be started up by a unit test. That had two costs, and both were paid
// for real. A settings field that needs anything other than a plain copy has a
// merge rule, and the rule had to be written out by hand in BOTH loops — once on
// the way in and once on the way out, about 35 lines apart. Editing only one of
// them loses data and nothing complains. And because neither loop could be
// driven by a test, the only check that existed read `settings.service.ts` as
// TEXT and matched a field name with a regular expression.
//
// So the loops are here, the merge rules are looked up in ONE place
// (`findSettingsMergeRule` below), and both directions consult that one place. A
// new field with a merge rule is now registered once, and both directions pick
// it up on their own.
//
// Angular-free on purpose, so the loops can be driven directly by a test — the
// same reason `review-prompt-settings.ts` and `referral-settings.ts` next door
// are plain modules.
//
// **What this module does NOT decide.** It never decides when it runs, which
// keys it is given, or whether a save happens afterwards. The caller keeps every
// one of those: which key list to use while a coach manages a client, whether to
// install the save-side callback at all, and the database write at the end. That
// split is deliberate and matches the comment above the call sites in
// `app.component.ts`.

import {
  isReviewPromptTimestampSettingKey, mergeReviewPromptAskLists, mergeReviewPromptTimestamp,
  REVIEW_PROMPT_ASK_LIST_SETTING_KEY,
} from './review-prompt-settings';
import { isStickyReferralSettingKey, mergeStickyReferralFlag } from './referral-settings';

/**
 * A bag of settings values addressed by key.
 *
 * Both sides of the copy are one of these: the in-memory `SettingsService` and
 * the `settings` document held by the user database. Typed loosely on purpose —
 * this module's whole job is to move values by key without knowing what any of
 * them mean.
 */
export interface SettingsByKey {
  [key: string]: any;
}

/**
 * How to combine this device's value with the database's value for one key.
 *
 * Always called as `(mine, theirs)`: `mine` is whatever this device holds in
 * memory, `theirs` is whatever the synced document holds. The answer is the
 * value that wins.
 */
export type SettingsMergeRule = (mine: unknown, theirs: unknown) => any;

/**
 * The merge rule for one settings key, or `undefined` when that key takes the
 * ordinary last-writer-wins copy.
 *
 * **This is the one place a field with a merge rule is registered.** Both copy
 * directions below ask this function, so adding a rule here is enough — there is
 * no second list to remember.
 *
 * Why any of these fields need a rule at all: the `settings` document syncs and
 * is last-writer-wins, so a device that has not synced yet writes its own older
 * value straight over a newer one that arrived from another device. For most
 * settings that is harmless. For a field recording that something HAPPENED it is
 * not, because nothing ever writes it again:
 *
 * - the review-prompt moments (#4625) record when we last asked somebody to rate
 *   the app, which is what stops us asking again within a year. Losing the date
 *   asks them a second time, and nothing reports it. Phase 2's list of every ask
 *   (#4816) is merged as a union for the same reason.
 * - the referral markers (#4963) record that somebody was invited and that the
 *   server knows they trained. A claim happens once, so losing the marker means
 *   their inviter simply never sees them start.
 */
export function findSettingsMergeRule(key: string): SettingsMergeRule | undefined {
  if (isReviewPromptTimestampSettingKey(key)) return mergeReviewPromptTimestamp;
  if (key === REVIEW_PROMPT_ASK_LIST_SETTING_KEY) return mergeReviewPromptAskLists;
  if (isStickyReferralSettingKey(key)) return mergeStickyReferralFlag;
  return undefined;
}

/**
 * Copy settings from the database into memory — the direction that runs at boot.
 *
 * A key with a merge rule is merged whether or not the database holds anything
 * for it, because the rules are written so that "nothing stored" is an ordinary
 * answer and can never win over a real value. A key without one is copied only
 * when the database actually has a different, defined value for it.
 *
 * Nothing is written back to the database here. The next ordinary save does
 * that, through `copySettingsToDatabase` below.
 */
export function copySettingsFromDatabase(
  memory: SettingsByKey,
  fromDatabase: SettingsByKey,
  keysToCopy: ReadonlyArray<string>,
): void {
  for (const key of keysToCopy) {
    const mergeRule = findSettingsMergeRule(key);
    if (mergeRule) {
      memory[key] = mergeRule(memory[key], fromDatabase[key]);
      continue;
    }
    if (key in fromDatabase
        && fromDatabase[key] !== undefined
        && memory[key] !== fromDatabase[key]) {
      memory[key] = fromDatabase[key];
    }
  }
}

/**
 * Copy settings from memory into the database — the direction that runs on every
 * save.
 *
 * **This is the direction that actually loses data without the merge rules.** The
 * load-side copy above runs once at boot, so a newer value arriving from sync
 * afterwards sits in the database while this device still holds the older one; an
 * ordinary save would then write the older value straight over it.
 *
 * A merged value is written back into MEMORY as well as into the database, so
 * whatever reads the value next sees the same one the database now holds instead
 * of staying behind until the next cold start.
 *
 * Only keys the in-memory bag actually carries are considered, which is what
 * keeps a key that a device does not know about from being written as
 * `undefined`.
 */
export function copySettingsToDatabase(
  memory: SettingsByKey,
  toDatabase: SettingsByKey,
  keysToCopy: ReadonlyArray<string>,
): void {
  for (const key of keysToCopy) {
    if (!(key in memory)) continue;
    const mergeRule = findSettingsMergeRule(key);
    if (mergeRule) {
      const merged = mergeRule(memory[key], toDatabase[key]);
      toDatabase[key] = merged;
      memory[key] = merged;
      continue;
    }
    toDatabase[key] = memory[key];
  }
}
